import moment from 'moment';
import 'moment/locale/en-gb';
import { CountryCode } from '../src/CountryCode'
moment.locale("en-gb")

const isEmpty = (value: string | number) => {
    // check if the value is blank, null or undefined,
    // we can't use javascripts built in truthy/falsey check as we could pass in a number of 0 and that would return falsey
    return (value == null || value.toString().length === 0);
}

const currency = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    const floatValue = parseFloat(value.toString())
    if (isNaN(floatValue)) {
        return false;
    }

    if (floatValue > 9999999999999) {
        return false;
    }
    return true;
}

const currencyNeg = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    const floatValue = parseFloat(value.toString())
    if (isNaN(floatValue)) {
        return false;
    }

    if (floatValue < 0 || floatValue > 9999999999999) {
        return false;
    }

    return true;
}

const date = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    return moment(value).isValid();
}

const nameUpperLowerCase = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // if the Name contains any of the following invalid characters (i.e. numerics) then we should return false as this is not a valid name;
    const regex = new RegExp(/[01234567890!£$%^*()_+=\[\]{};:@~#?\/<>\|]/);

    return !regex.test(value);
}

const numeric = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // checks if the value is a positive numeric value
    const floatValue = parseFloat(value.toString());

    if (isNumeric(value) == false) {
        return false;
    }

    if (isNaN(floatValue)) {
        return false;
    }

    if (floatValue < 0) {
        return false;
    }

    return true;
};

const numericNeg = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // checks if the value is a numeric value (can be negative or positive)
    return isNumeric(value);
}

const isNumeric = (value: string | number) => {
    // checks if the value follows a valid numeric format
    const regex = new RegExp(/^-?[.]?[0-9]+[.]?[0-9]*$/);

    return regex.test(value.toString());
}

const percentage = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    return isNumeric(value);
}

const email = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // check if the email address is valid
    const regex = new RegExp(/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/);

    return regex.test(value);
}

const webPage = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // check if the webpage address is valid
    const regex = new RegExp(/^((ht|f)tp(s?)\:\/\/|~\/|\/)?([\w]+:\w+@)?(([\w\-]+\.)+([\w]{2,5}))(:[\d]{1,5})?((\/?\w+\/)+|\/?)(\w+\.[\w]{3,4})?((\?\w+=\w+)?(&\w+=(\w|\/|\.)+)*)?/, "i");

    return regex.test(value);
}

const accountSortCode = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // account sort code must be a 6 digit number
    const regex = new RegExp("^[0-9]{6,6}$");

    return regex.test(value.toString());
}

const accountNumber = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // account number must be an 8 digit number
    const regex = new RegExp("^[0-9]{8,8}$");

    return regex.test(value.toString());
}

// validates IBAN according to IBAN validation - http://en.wikipedia.org/wiki/IBAN 
const IBAN = (value: string) => {
    let iban2: string = "";
    let iban3: string = "";
    let ibanChar: string = "";
    let ibanCharCode: number = 0;
    let remainder: number = 0;

    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    value = value.toUpperCase();

    // if the IBAN is 4 characters or less, then this invalid
    if (value.length <= 4) {
        return false;
    }

    iban2 = (value.substring(4) + value.substring(0, 4)).toUpperCase();

    for (let counter = 0; counter < iban2.length; counter++) {
        ibanChar = iban2.substring(counter, counter + 1);
        if (ibanChar !== " ") {
            ibanCharCode = ibanChar.charCodeAt(0);
            if ((ibanCharCode >= 65) && (ibanCharCode <= 90)) {
                iban3 = iban3 + (ibanCharCode - 55).toString();
            } else {
                iban3 = iban3 + ibanChar;
            }
        }
    }

    // if the iban3 is not numeric, then we should exit as the iban is invalid
    if (isNumeric(iban3) == false) {
        return false;
    }

    for (let counter = 0; counter < iban3.length; counter++) {
        remainder = (10 * remainder + Number(iban3.substring(counter, counter + 1))) % 97
    }

    // only valid if the remainder is 1
    return (remainder === 1);
}

const EPC = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    value = value.toString().replaceAll("-", "")

    // replace strange - character
    value = value.toString().replaceAll(String.fromCharCode(173), "")

    // must be a 20 digit number
    const regex = new RegExp("^[0-9]{20,20}$");

    return regex.test(value.toString());
}

const propertyUniqueReferenceNumber = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // a PropertyUniqueReferenceNumber can only be up to 12 digits in length
    const regex = new RegExp("^[0-9]{1,12}$");

    return regex.test(value.toString());
}

const briefYourMarketUserName = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // ensure that the BriefYourMarketUserName contains briefyourmarket.com (the double backslash '\' is escaping the character)
    return value.toLowerCase().includes('briefyourmarket.com\\');
}

const internationalPassportNumber = (value: string | number) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // a Passport Number must follow passport number validation
    const regex = new RegExp("^[A-Z0-9<]{9}[0-9]{1}[A-Z]{3}[0-9]{7}[A-Z]{1}[0-9]{7}[A-Z0-9<]{14}[0-9<]{1}[0-9]{1}$", "i")

    return regex.test(value.toString().toUpperCase());
}

const username = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // a Username must be at least 2 characters in length
    return (value.length >= 2);
}

const userPassword = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // otherwise, ensure that the password is at least 4 characters in length
    return (value.length >= 4);
}

const mprn = (value: string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // must be an 11 digit number
    const regex = new RegExp("^[0-9]{11,11}$");

    return regex.test(value.toString());
}

const gprn = (value :string) => {
    // If the value is empty then treat as successfully validated, so we can save blank strings
    if (isEmpty(value)) {
        return true;
    }

    // must be an 11 digit number
    const regex = new RegExp("^[0-9]{7,7}$");

    return regex.test(value.toString());    
}

export { currency, currencyNeg, date, nameUpperLowerCase, numeric, numericNeg, isNumeric, percentage, email, webPage, accountSortCode, accountNumber, IBAN, EPC, propertyUniqueReferenceNumber, briefYourMarketUserName, internationalPassportNumber, username, userPassword, mprn, gprn }